'use client';

import { type ComponentType, type CSSProperties, type ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  type Header,
  type Row,
  type SortingState as RTSortingState,
  useReactTable,
} from '@tanstack/react-table';
import { clsx } from 'clsx';
import type { OnChangeFn } from '@interxfi/table-config/base-types';
import type { SortingState } from '@interxfi/table-config/sort';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { STABLE_EMPTY_OBJECT_REF } from '@interxfi/utilities/object/stable-empty';
import type { Color, Space } from '../../styles/tokens';
import { useIntersectionObserverAction } from '../../util/intersection-observer';
import { useLayoutMeasurementContext } from '../../util/layout-measurement-context';
import type { ResponsiveValue } from '../../util/responsive';
import { getCSSVariableValue } from '../../util/style';
import { VerticalStack } from '../alignment/vertical-stack';
import { Skeleton } from '../loading/skeleton';
import { Typography } from '../typography';
import type { RowLevelInteractionBuilder } from './row-context';
import { TableBody } from './table-body/table-body';
import { TableFooter } from './table-footer/table-footer';
import { TableHeader } from './table-header/table-header';
import { DEFAULT_COLUMN_DEF, type TableColumnDef } from './util/column';
import { type TableOnColumnReorder, useColumnOrder } from './util/column-order';
import { type TableColumnPinning, type TableOnColumnPinningChange, useColumnPinning } from './util/column-pinning';
import { type TableColumnSizes, type TableOnColumnResize, useColumnSize } from './util/column-sizing';
import { type TableColumnVisibility, type TableOnColumnVisibilityChange, useColumnVisibility } from './util/column-visibility';
import { getSafeColumnId } from './util/general';
import { allRowsCanExpand } from './util/row-expander/row-expander';
import { type TableOnRowSelectionChange, type TableRowSelections, useRowSelections } from './util/row-selection';
import styles from './table.module.scss';

const DEFAULT_LOADING_ROWS = 10;

function getTableData<T extends object>(data: T[] | undefined, showSkeleton: boolean | undefined, loadingRows: number): T[] {
  if (showSkeleton) {
    return new Array(loadingRows).fill({}).map(() => STABLE_EMPTY_OBJECT_REF) as T[];
  }

  return data || STABLE_EMPTY_ARRAY_REF;
}

function LoadingCell() {
  return <Skeleton parentClassName={styles.pendingData} />;
}

function addLoadingCell<TData extends object, TValue>(column: TableColumnDef<TData, TValue>): TableColumnDef<TData, TValue> {
  if (column.columns) {
    return {
      ...column,
      accessorFn: () => undefined as TValue,
      cell: column.skeleton ?? LoadingCell,
      columns: column.columns.map(addLoadingCell),
    };
  }

  return {
    ...column,
    accessorFn: () => undefined as TValue,
    cell: column.skeleton ?? LoadingCell,
  };
}

function getTableColumns<TData extends object, TValue>(
  columns: TableColumnDef<TData, TValue>[],
  showSkeleton?: boolean,
): TableColumnDef<TData, TValue>[] {
  if (showSkeleton) {
    return columns.map(addLoadingCell);
  }

  return columns;
}

function buildTableColumnSizeVariables<T extends object>(scrollContainerWidth: number, tableStateSize: number, headers: Header<T, any>[]) {
  const tableWidth = scrollContainerWidth || tableStateSize || 0;

  return headers.reduce(
    (acc, header) => {
      const headerSize = header.getSize();
      const headerSizePercentage = headerSize / tableStateSize;
      let renderedSize = headerSizePercentage * tableWidth;

      const headerId = getSafeColumnId(header.column.id || header.id || '');

      // If it's a group header, use the children's sizes to determine the rendered size
      if (header.subHeaders.length) {
        const hasColumnWithoutMaxSize = header.subHeaders.some(
          (subHeader) => !subHeader.column.columnDef.maxSize || subHeader.column.columnDef.maxSize === Number.MAX_SAFE_INTEGER,
        );
        renderedSize = header.subHeaders.reduce((sum, subHeader) => sum + subHeader.getSize(), 0);

        if (!hasColumnWithoutMaxSize) {
          acc[`--ixf-table-column-${headerId}-max-size`] = `${renderedSize}px`;
        }

        acc[`--ixf-table-column-${headerId}-min-size`] = `${renderedSize}px`;
      } else {
        if (header.column.columnDef.maxSize) {
          acc[`--ixf-table-column-${headerId}-max-size`] = `${header.column.columnDef.maxSize}px`;

          if (renderedSize > header.column.columnDef.maxSize) {
            renderedSize = header.column.columnDef.maxSize;
          }
        }

        if (header.column.columnDef.minSize) {
          acc[`--ixf-table-column-${headerId}-min-size`] = `${header.column.columnDef.minSize}px`;

          if (renderedSize < header.column.columnDef.minSize) {
            renderedSize = header.column.columnDef.minSize;
          }
        }
      }

      acc[`--ixf-table-column-${headerId}-size`] = `1 1 ${renderedSize}px`;

      return acc;
    },
    {} as Record<string, string>,
  );
}

function buildTableStyleVars(
  scrollContainerWidth: number | undefined,
  totalSize: number,
  flatHeaders: Header<any, any>[],
  backgroundColor: Color,
): CSSProperties {
  return {
    ...buildTableColumnSizeVariables(scrollContainerWidth || totalSize, totalSize, flatHeaders),
    '--ixf-table-background-color': `var(--ixf-color-${backgroundColor})`,
  } as CSSProperties;
}

const DEFAULT_RENDER_WHEN_EMPTY = 'No Results Found';

const DEFAULT_RENDER_WHEN_NO_FILTERS_MATCH = (
  <VerticalStack align="center" gap={2} inlineAlign="center">
    <Typography lineHeight="1.625rem" size="1.125rem" tone="neutral-dk" variant="heading">
      No Results
    </Typography>

    <Typography align="center" lineHeight="1.5rem" size="1rem" variant="heading">
      Try adjusting your search or filter parameters
    </Typography>
  </VerticalStack>
);

function getInitialTableColumnOrder<TData extends object>(columns: TableColumnDef<TData>[]): string[] {
  return columns.map((column) => column.id);
}

interface TablePagination {
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => Promise<any>;
}

const defaultContainerGap: ResponsiveValue<Space> = 7;

export interface TableProps<
  TData extends object,
  TValue = any,
  TSearchableField extends string = string,
  TSortableField extends string = string,
  TFilterableField extends string = string,
> {
  backgroundColor?: Color;
  columns: TableColumnDef<TData, TValue>[];
  containerGap?: ResponsiveValue<Space>;
  controlledColumnOrder?: string[];
  controlledColumnPinning?: TableColumnPinning<TData>;
  controlledColumnSize?: TableColumnSizes<TData>;
  controlledColumnSort?: SortingState<TSortableField>;
  controlledColumnVisibility?: TableColumnVisibility<TData>;
  controlledRowSelections?: TableRowSelections;
  data: TData[] | undefined;
  defaultCellRenderWhenEmpty?: ReactNode;
  getRowCanExpand?: true | ((row: Row<TData>) => boolean);
  groupRowsBy?: (row: TData) => string;
  headerActions?: ReactNode;
  loadingRows?: number;
  manualSorting?: boolean;
  onColumnPin?: TableOnColumnPinningChange<TData>;
  onColumnReorder?: TableOnColumnReorder;
  onColumnResize?: TableOnColumnResize<TData>;
  onColumnSort?: OnChangeFn<SortingState<TSortableField>>;
  onColumnVisibilityChange?: TableOnColumnVisibilityChange<TData>;
  onRowSelectionChange?: TableOnRowSelectionChange;
  pagination?: TablePagination;
  SubRowComponent?: ComponentType<TData>;
  renderWhenEmpty?: ReactNode;
  renderWhenNoFiltersMatch?: ReactNode;
  rowLevelInteractionBuilder?: RowLevelInteractionBuilder<TData>;
  showSkeleton?: boolean;
  withoutHorizontalBorders?: boolean;
}

export function Table<
  TData extends object,
  TValue = unknown,
  TSearchableField extends string = string,
  TSortableField extends string = string,
  TFilterableField extends string = string,
>({
  backgroundColor = 'background',
  columns,
  containerGap = defaultContainerGap,
  controlledColumnOrder,
  controlledColumnPinning,
  controlledColumnSize,
  controlledColumnSort,
  controlledColumnVisibility,
  controlledRowSelections,
  data,
  defaultCellRenderWhenEmpty = '--',
  getRowCanExpand,
  groupRowsBy,
  headerActions,
  loadingRows = DEFAULT_LOADING_ROWS,
  manualSorting = true,
  onColumnPin,
  onColumnReorder,
  onColumnResize,
  onColumnSort,
  onColumnVisibilityChange,
  onRowSelectionChange,
  pagination,
  renderWhenEmpty = DEFAULT_RENDER_WHEN_EMPTY,
  renderWhenNoFiltersMatch = DEFAULT_RENDER_WHEN_NO_FILTERS_MATCH,
  rowLevelInteractionBuilder,
  showSkeleton,
  SubRowComponent,
  withoutHorizontalBorders,
}: TableProps<TData, TValue, TSearchableField, TSortableField, TFilterableField>) {
  // 'use no memo' directive is required for now because react-table v8 is not compatible with React 19's compiler just yet
  'use no memo';

  const { topbarHeight: topOffset } = useLayoutMeasurementContext();
  const lastKnownScrollYRef = useRef(typeof window === 'undefined' ? 0 : window.scrollY);
  const tickingRef = useRef(false);
  const id = useId();
  const defaultColumnOrder = useMemo(() => getInitialTableColumnOrder(columns), [columns]);
  const [hasHadData, setHasHadData] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [hasXOverflow, setHasXOverflow] = useState(
    Boolean(tableContainerRef.current && tableContainerRef.current.scrollWidth > tableContainerRef.current.clientWidth),
  );
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = pagination || {};
  const [setObservedItemRef] = useIntersectionObserverAction(fetchNextPage, {
    disabled: showSkeleton || !hasNextPage || isFetchingNextPage || !fetchNextPage,
  });
  const [columnOrder, handleColumnOrderChange] = useColumnOrder(controlledColumnOrder ?? defaultColumnOrder, onColumnReorder);
  const [columnSizing, handleColumnSizingChange] = useColumnSize(controlledColumnSize, onColumnResize);
  const [columnPinning, handleColumnPinChange] = useColumnPinning(columns, controlledColumnPinning, onColumnPin);
  const [rowSelection, handleRowSelectionChange] = useRowSelections(controlledRowSelections, onRowSelectionChange);
  const hasNoCurrentData = !data?.length;
  const dataExistsButNotMatchingFilters = !showSkeleton && hasHadData && hasNoCurrentData;
  const isShowingSkeleton = showSkeleton || hasNoCurrentData || dataExistsButNotMatchingFilters;
  const showEmptyMessage = !showSkeleton && !dataExistsButNotMatchingFilters && hasNoCurrentData && renderWhenEmpty;
  const [columnVisibility, handleColumnVisibilityChange] = useColumnVisibility(columns, controlledColumnVisibility, onColumnVisibilityChange);
  const tableData = useMemo(() => getTableData(data, isShowingSkeleton, loadingRows), [data, isShowingSkeleton, loadingRows]);
  const tableColumns = useMemo(() => getTableColumns(columns, isShowingSkeleton), [columns, isShowingSkeleton]);
  const [internalSort, setInternalSort] = useState<SortingState<TSortableField>>([]);
  const table = useReactTable({
    columns: tableColumns,
    columnResizeMode: onColumnResize ? 'onChange' : undefined,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getExpandedRowModel: getRowCanExpand ? getExpandedRowModel() : undefined,
    getRowCanExpand: getRowCanExpand === true ? allRowsCanExpand : getRowCanExpand,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnPinningChange: handleColumnPinChange,
    onRowSelectionChange: handleRowSelectionChange,
    onSortingChange: manualSorting
      ? (onColumnSort as unknown as OnChangeFn<RTSortingState>)
      : (setInternalSort as unknown as OnChangeFn<RTSortingState>),
    onColumnOrderChange: handleColumnOrderChange,
    state: { columnOrder, columnSizing, columnVisibility, sorting: manualSorting ? controlledColumnSort : internalSort, columnPinning, rowSelection },
    defaultColumn: DEFAULT_COLUMN_DEF,
    manualSorting,
    enableSorting: Boolean(onColumnSort || !manualSorting),
    enableExpanding: Boolean(getRowCanExpand && SubRowComponent),
    enableColumnResizing: Boolean(onColumnResize),
    enableColumnPinning: true,
    enableRowSelection: Boolean(onRowSelectionChange),
    enableMultiSort: true,
  });

  const rowModel = table.getSortedRowModel();
  const headerGroups = table.getHeaderGroups();
  const footerGroups = table.getFooterGroups();
  const totalSize = table.getTotalSize();
  const flatHeaders = table.getFlatHeaders();
  const styleVariables = useMemo(
    () => buildTableStyleVars(tableContainerRef.current?.scrollWidth, totalSize, flatHeaders, backgroundColor),
    [totalSize, flatHeaders, backgroundColor],
  );

  useEffect(() => {
    if (data?.length) {
      setHasHadData(true);
    }
  }, [data?.length]);

  useEffect(() => {
    function handleResize() {
      if (!tableContainerRef.current) {
        return;
      }

      setHasXOverflow(Boolean(tableContainerRef.current && tableContainerRef.current.scrollWidth > tableContainerRef.current.clientWidth));
    }

    const observer = new ResizeObserver(handleResize);

    if (tableContainerRef.current) {
      observer.observe(tableContainerRef.current);
    }

    handleResize();

    return () => {
      observer.disconnect();
    };
  }, []);

  // Sticky header handler
  useLayoutEffect(() => {
    const stickyHeader: HTMLElement | null | undefined = tableContainerRef.current?.querySelector('[data-sticky-header="true"]');
    /*
     * This is an... interesting... solution for enabling sticky table headers while allowing the table to horizontally scroll.
     * Doing this with state, like the column sizing variables, would be less hacky, but it results in rendering lag that makes
     * the table header appear to jump around as the user scrolls. This solution is a bit hacky, but it's what we've got for now.
     * TODO: investigate applying a max-height to the table so sticky table headers work correctly without this hackiness.
     */
    function updateStickyHeaderPosition() {
      // Add 1 px to the resulting top offset to hide the top border.
      if (stickyHeader) {
        if (lastKnownScrollYRef.current >= topOffset) {
          stickyHeader.style.top = '0px';
        } else {
          // 1 px offset to hide the top border
          stickyHeader.style.top = `${topOffset - lastKnownScrollYRef.current - 1}px`;
        }
      }
    }

    const handleUpdateStickyHeaderPosition = () => {
      const newLastKnownScrollY = tableContainerRef.current?.getBoundingClientRect()?.top || 0;

      if (lastKnownScrollYRef.current === newLastKnownScrollY) {
        return;
      }

      lastKnownScrollYRef.current = newLastKnownScrollY;

      if (!tickingRef.current && stickyHeader && stickyHeader.style.top !== `${lastKnownScrollYRef.current}px`) {
        window.requestAnimationFrame(() => {
          updateStickyHeaderPosition();
          tickingRef.current = false;
        });

        tickingRef.current = true;
      }
    };

    window.addEventListener('scroll', handleUpdateStickyHeaderPosition, true);
    handleUpdateStickyHeaderPosition();

    return () => {
      window.removeEventListener('scroll', handleUpdateStickyHeaderPosition, true);
    };
  }, [topOffset]);

  return (
    <VerticalStack
      className={clsx(styles.container, dataExistsButNotMatchingFilters && styles.hasNoDataForFilters)}
      gap={containerGap}
      id={id}
      style={styleVariables}
    >
      {headerActions}

      <div
        className={clsx(
          styles.tableContainer,
          (showEmptyMessage || dataExistsButNotMatchingFilters) && styles.isEmpty,
          hasXOverflow && styles.hasXOverflow,
        )}
        ref={tableContainerRef}
      >
        {showEmptyMessage ? (
          <div className={styles.noDataContainer}>
            <div className={styles.content}>
              <Typography
                align="center"
                inheritance="current"
                lineHeight="1.625rem"
                size="1.125rem"
                tone="neutral-dk"
                variant="heading"
                weight="bold"
              >
                {renderWhenEmpty}
              </Typography>
            </div>
          </div>
        ) : null}

        {dataExistsButNotMatchingFilters ? (
          <div className={styles.noMatchContainer}>
            <div className={styles.content}>{renderWhenNoFiltersMatch}</div>
          </div>
        ) : null}

        <table className={clsx(styles.table, withoutHorizontalBorders && styles.horizontalBorderless)}>
          <TableHeader<TData> groups={headerGroups} />

          <TableBody
            defaultCellRenderWhenEmpty={defaultCellRenderWhenEmpty}
            groupRowsBy={groupRowsBy}
            rowLevelInteractionBuilder={rowLevelInteractionBuilder}
            rows={rowModel.rows}
            SubRowComponent={SubRowComponent}
            showSkeleton={isShowingSkeleton}
            totalRows={rowModel.rows.length}
          />

          <TableFooter<TData>
            groups={footerGroups}
            isFetchingNextPage={isFetchingNextPage}
            isShowingData={!showEmptyMessage && !dataExistsButNotMatchingFilters}
            ref={setObservedItemRef}
          />
        </table>
      </div>
    </VerticalStack>
  );
}
